import { useState, useEffect, useRef } from "react";
import { useApp, api } from "../context";
import { Shell } from "../components";
import { TriviaGameModal, TriviaGameOver } from "../TriviaGame";

export default function DuoPlayPage() {
  const { dark, user, flash, go } = useApp();
  const [gameMode, setGameMode] = useState(null); // null | "trivia" | "ball_sort"
  const [bsRounds, setBsRounds] = useState(5);
  const [bsSending, setBsSending] = useState(false);
  const [bsPendingChallenge, setBsPendingChallenge] = useState(null);
  const bsFastPollRef = useRef(null);
  const bsCountdownRef = useRef(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [betAmount, setBetAmount] = useState(0);
  const [sending, setSending] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null);
  const [freshGtBalance, setFreshGtBalance] = useState(null);
  const [challenges, setChallenges] = useState({ sent: [], received: [] });
  const [activeGame, setActiveGame] = useState(null);
  const [gameOver, setGameOver] = useState(null);
  const [tick, setTick] = useState(0);
  const searchTimeout = useRef(null);
  const activeGameRef = useRef(null);
  const fastPollRef = useRef(null);
  useEffect(() => { activeGameRef.current = activeGame; }, [activeGame]);

  const countdownTimerRef = useRef(null);

  const startChallengeCountdown = (seconds) => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    let remaining = seconds;
    countdownTimerRef.current = setInterval(() => {
      remaining--;
      setPendingChallenge(prev => prev ? { ...prev, countdown: remaining } : null);
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        if (fastPollRef.current) clearInterval(fastPollRef.current);
        setPendingChallenge(prev => {
          if (prev) {
            api(`/trivia/${prev.gameId}/cancel`, { method: "POST" });
            flash("Challenge expired — GT refunded");
          }
          return null;
        });
      }
    }, 1000);
  };
  const startFastPoll = (gameId) => {
    if (fastPollRef.current) clearInterval(fastPollRef.current);
    fastPollRef.current = setInterval(async () => {
      const r = await api(`/trivia/${gameId}/state`);
      if (!r.ok) return;
      const g = r.data.game;
      if (g?.status === "active") {
        clearInterval(fastPollRef.current);
        setActiveGame(g);
        setPendingChallenge(null);
      } else if (g?.status === "cancelled" || g?.status === "expired") {
        clearInterval(fastPollRef.current);
        setPendingChallenge(null);
        flash("Challenge was declined or expired", "error");
      }
    }, 1000);
  };

  const bg = dark ? "#080810" : "#f4f5f9";
  const card = dark ? "#0f0f1a" : "#ffffff";
  const border = dark ? "#ffffff12" : "#e0e2eb";
  const text = dark ? "#ffffff" : "#1a1c2e";
  const sub = dark ? "#ffffff60" : "#6b7094";
  const faint = dark ? "#ffffff30" : "#9ca0b8";
  const input = dark ? "#111120" : "#f0f1f6";

  useEffect(() => {
    loadBalance();
    loadChallenges();

    // Auto-open game if coming from notification/popup
    const autoGameId = sessionStorage.getItem("gc_auto_game_id");
    if (autoGameId) {
      sessionStorage.removeItem("gc_auto_game_id");
      api(`/trivia/${autoGameId}/state`).then(r => {
        if (r.ok && r.data.game) setActiveGame(r.data.game);
      });
    }

    // Tick every 1s to update receiver countdown
    const tickIv = setInterval(() => setTick(t => t + 1), 1000);

    // Poll every 2s to auto-detect active games
    const iv = setInterval(async () => {
      const r = await api("/trivia/my-challenges");
      if (!r.ok) return;
      setChallenges(r.data);
      const all = [...(r.data.sent || []), ...(r.data.received || [])];
      const active = all.find(g => g.status === "active");
      if (active && !activeGameRef.current) {
        const gr = await api(`/trivia/${active.id}/state`);
        if (gr.ok) { 
          setActiveGame(gr.data.game); 
          setPendingChallenge(null); 
          if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        }
      }
      const cancelled = all.find(g => g.status === "cancelled" || g.status === "expired");
      if (cancelled) setPendingChallenge(prev => prev?.gameId === cancelled.id ? null : prev);
    }, 2000);
    return () => { clearInterval(iv); clearInterval(tickIv); if (fastPollRef.current) clearInterval(fastPollRef.current); if (countdownTimerRef.current) clearInterval(countdownTimerRef.current); };
  }, []); // eslint-disable-line

  const loadBalance = async () => {
    const r = await api("/auth/me");
    if (r.ok) setFreshGtBalance(parseFloat(r.data.user?.gt_balance || 0));
  };

  const loadChallenges = async () => {
    const r = await api("/trivia/my-challenges");
    if (r.ok) setChallenges(r.data);
  };

  const handleSearch = (val) => {
    setSearch(val);
    setSelectedUser(null);
    clearTimeout(searchTimeout.current);
    if (!val.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      const r = await api(`/search?q=${encodeURIComponent(val)}`);
      if (r.ok) setSearchResults(r.data.users || []);
      setSearching(false);
    }, 400);
  };

  const handleSendChallenge = async () => {
    if (!selectedUser) { flash("Select a player first", "error"); return; }
    if (betAmount > 0 && freshGtBalance < betAmount) { flash("Insufficient GT balance", "error"); return; }
    setSending(true);
    const r = await api("/trivia/direct-challenge", {
      method: "POST",
      body: JSON.stringify({ opponent_id: selectedUser.id, bet_amount: betAmount })
    });
    setSending(false);
    if (r.ok) {
      const gId = r.data.game_id;
      setPendingChallenge({ 
        gameId: gId, 
        opponentName: selectedUser.name,
        betAmount: betAmount,
        sentAt: Date.now()
      });
      startFastPoll(gId);
      startChallengeCountdown(20);
      setSelectedUser(null);
      setSearch("");
      setSearchResults([]);
      setBetAmount(0);
      loadChallenges();
    } else {
      flash(r.message || "Failed to send challenge", "error");
    }
  };

  const handleAccept = async (gameId) => {
    const r = await api(`/trivia/${gameId}/direct-accept`, { method: "POST" });
    if (r.ok) {
      setActiveGame(r.data.game);
      setPendingChallenge(null);
      loadChallenges();
    } else flash(r.message || "Failed to accept", "error");
  };

  const handleDecline = async (gameId) => {
    const r = await api(`/trivia/${gameId}/decline`, { method: "POST" });
    if (r.ok) { flash("Challenge declined"); loadChallenges(); }
    else flash(r.message || "Failed", "error");
  };

  const handleSendBsChallenge = async () => {
    if (!selectedUser) { flash("Select a player first", "error"); return; }
    if (betAmount > 0 && freshGtBalance < betAmount) { flash("Insufficient GT balance", "error"); return; }
    setBsSending(true);
    const r = await api("/duo-ball-sort/challenge", {
      method: "POST",
      body: JSON.stringify({ opponent_id: selectedUser.id, bet_amount: betAmount, rounds: bsRounds })
    });
    setBsSending(false);
    if (r.ok) {
      const gId = r.data.game_id;
      setBsPendingChallenge({ gameId: gId, opponentName: selectedUser.name, betAmount, countdown: 30 });
      setSelectedUser(null); setSearch(""); setSearchResults([]); setBetAmount(0);
      // Fast poll for acceptance
      if (bsFastPollRef.current) clearInterval(bsFastPollRef.current);
      bsFastPollRef.current = setInterval(async () => {
        const sr = await api(`/duo-ball-sort/${gId}/state`);
        if (!sr.ok) return;
        if (sr.data.status === "active") {
          clearInterval(bsFastPollRef.current);
          clearInterval(bsCountdownRef.current);
          setBsPendingChallenge(null);
          flash("Game started! Good luck! 🎨");
          // Navigate to game
          sessionStorage.setItem("gc_bs_game", JSON.stringify(sr.data));
          go("duo_ball_sort");
        } else if (sr.data.status === "cancelled") {
          clearInterval(bsFastPollRef.current);
          setBsPendingChallenge(null);
          flash("Challenge declined", "error");
        }
      }, 2000);
      // Countdown 30s
      if (bsCountdownRef.current) clearInterval(bsCountdownRef.current);
      let remaining = 30;
      bsCountdownRef.current = setInterval(() => {
        remaining--;
        setBsPendingChallenge(prev => prev ? { ...prev, countdown: remaining } : null);
        if (remaining <= 0) {
          clearInterval(bsCountdownRef.current);
          clearInterval(bsFastPollRef.current);
          api(`/duo-ball-sort/${gId}/cancel`, { method: "POST" });
          setBsPendingChallenge(null);
          flash("Challenge expired", "error");
        }
      }, 1000);
    } else flash(r.message || "Failed to send challenge", "error");
  };

  const handleCancelBsChallenge = async () => {
    if (!bsPendingChallenge) return;
    clearInterval(bsFastPollRef.current);
    clearInterval(bsCountdownRef.current);
    const r = await api(`/duo-ball-sort/${bsPendingChallenge.gameId}/cancel`, { method: "POST" });
    if (r.ok) { flash("Challenge cancelled, GT refunded"); setBsPendingChallenge(null); }
    else flash(r.message || "Failed", "error");
  };

  const handleCancelChallenge = async () => {
    if (!pendingChallenge) return;
    if (fastPollRef.current) clearInterval(fastPollRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    const r = await api(`/trivia/${pendingChallenge.gameId}/cancel`, { method: "POST" });
    if (r.ok) { flash("Challenge cancelled, GT refunded"); setPendingChallenge(null); loadChallenges(); }
    else flash(r.message || "Failed to cancel", "error");
  };

  const statusColor = (s) => s === "pending" ? "#f59e0b" : s === "active" ? "#10b981" : s === "completed" ? "#6c5ce7" : "#ef4444";
  const statusLabel = (s) => s === "pending" ? "⏳ Waiting" : s === "active" ? "🎮 Active" : s === "completed" ? "✅ Done" : "❌ " + s;

  const Avatar = ({ u, size = 40 }) => (
    <div style={{ width: size, height: size, borderRadius: size/2, flexShrink: 0, overflow: "hidden", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4, color: "#fff" }}>
      {u?.avatar_path ? <img src={`/storage/${u.avatar_path}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (u?.avatar_emoji || u?.name?.[0] || "?")}
    </div>
  );

  if (activeGame) return (
    <TriviaGameModal
      game={activeGame}
      dark={dark}
      flash={flash}
      myAlias={user?.name}
      onClose={() => setActiveGame(null)}
      onGameEnd={(finalGame) => { setActiveGame(null); setGameOver(finalGame); loadChallenges(); }}
    />
  );

  if (gameOver) return (
    <TriviaGameOver
      game={gameOver}
      dark={dark}
      onClose={() => { setGameOver(null); loadChallenges(); }}
      onRematch={async () => {
        const opponentId = gameOver.is_challenger ? gameOver.opponent_user_id : gameOver.challenger_id;
        if (!opponentId) { flash("Could not find opponent", "error"); return; }
        const r = await api("/trivia/direct-challenge", {
          method: "POST",
          body: JSON.stringify({ opponent_id: opponentId, bet_amount: gameOver.bet_amount })
        });
        if (r.ok) {
          setGameOver(null);
          setPendingChallenge({
            gameId: r.data.game_id,
            opponentName: gameOver.is_challenger ? (gameOver.opponent?.name || gameOver.opponent_alias || "Opponent") : (gameOver.challenger?.name || gameOver.challenger_alias || "Opponent"),
            betAmount: gameOver.bet_amount,
            sentAt: Date.now(),
            countdown: 20
          });
          startFastPoll(r.data.game_id);
          startChallengeCountdown(20);
          loadChallenges();
        } else flash(r.message || "Failed to send rematch", "error");
      }}
    />
  );

  if (!gameMode) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'DM Sans',sans-serif" }}>
      <Shell title="⚔️ Duo Play">
        <div style={{ padding: "12px 0" }}>

          {/* Hero */}
          <div style={{ background: "linear-gradient(135deg,#0d0020,#1a0030,#0a0015)", borderRadius: 20, padding: "20px 16px", marginBottom: 20, border: "1px solid #e91e8c20" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⚔️</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 6 }}>
              Challenge. Race. <span style={{ color: "#e91e8c" }}>Win.</span>
            </div>
            <div style={{ fontSize: 13, color: "#ffffff60", lineHeight: 1.6 }}>
              Challenge members to 1v1 battles. Bet GT tokens. Winner takes all!
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: faint, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Choose Your Game</div>

          {/* Ball Sort Duo */}
          <div onClick={() => setGameMode("ball_sort")} style={{ background: card, borderRadius: 18, border: "1px solid #35c4ff30", padding: "18px 16px", marginBottom: 12, cursor: "pointer", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#35c4ff,#35c4ff00)" }} />
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "#35c4ff20", border: "1px solid #35c4ff30", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>🎨</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: text }}>Ball Sort Duo</div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#35c4ff", background: "#35c4ff20", padding: "2px 8px", borderRadius: 20 }}>NEW</span>
                </div>
                <div style={{ fontSize: 12, color: sub }}>Race your opponent to sort colored balls. First to finish wins the round!</div>
              </div>
              <span style={{ fontSize: 20, color: faint }}>›</span>
            </div>
          </div>

          {/* Trivia */}
          <div onClick={() => setGameMode("trivia")} style={{ background: card, borderRadius: 18, border: "1px solid #e91e8c30", padding: "18px 16px", marginBottom: 12, cursor: "pointer", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#e91e8c,#e91e8c00)" }} />
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "#e91e8c20", border: "1px solid #e91e8c30", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>🧠</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: text }}>Trivia Battle</div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#e91e8c", background: "#e91e8c20", padding: "2px 8px", borderRadius: 20 }}>Bet & Win</span>
                </div>
                <div style={{ fontSize: 12, color: sub }}>Challenge any member to a 1v1 Trivia battle. Bet GT tokens. Winner takes all!</div>
              </div>
              <span style={{ fontSize: 20, color: faint }}>›</span>
            </div>
          </div>

          <div style={{ height: 80 }} />
        </div>
      </Shell>
    </div>
  );

  if (gameMode === "ball_sort") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'DM Sans',sans-serif" }}>
      <Shell title="🎨 Ball Sort Duo" showBack onBack={() => setGameMode(null)}>

        {/* GT Balance */}
        <div style={{ background: dark ? "#1a1a0a" : "#fffbeb", border: "1px solid #f59e0b30", borderRadius: 14, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: sub }}>Your GT Balance</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>{freshGtBalance !== null ? freshGtBalance : "..."} GT</div>
        </div>

        {/* Challenge card */}
        <div style={{ background: card, borderRadius: 16, border: `1px solid ${border}`, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: text, marginBottom: 12 }}>🎨 Challenge a Player</div>

          <input
            style={{ width: "100%", padding: "10px 14px", borderRadius: 12, border: `1px solid ${border}`, background: input, color: text, fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}
            placeholder="Search by name or username..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />

          {searching && <div style={{ fontSize: 12, color: faint, textAlign: "center", padding: "8px 0" }}>Searching...</div>}
          {searchResults.length > 0 && !selectedUser && (
            <div style={{ borderRadius: 12, border: `1px solid ${border}`, overflow: "hidden", marginBottom: 10 }}>
              {searchResults.slice(0, 5).map(u => (
                <div key={u.id} onClick={() => { setSelectedUser(u); setSearch(u.name); setSearchResults([]); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${border}`, cursor: "pointer", background: card }}>
                  <Avatar u={u} size={36} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: faint }}>{u.plan || "free"}</div>
                  </div>
                  <span style={{ fontSize: 11, color: "#35c4ff", fontWeight: 700 }}>Select</span>
                </div>
              ))}
            </div>
          )}

          {selectedUser && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: dark ? "#35c4ff20" : "#e8f8ff", borderRadius: 12, padding: "10px 14px", marginBottom: 12, border: "1px solid #35c4ff40" }}>
              <Avatar u={selectedUser} size={40} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{selectedUser.name}</div>
                <div style={{ fontSize: 11, color: "#35c4ff" }}>Selected ✓</div>
              </div>
              <button onClick={() => { setSelectedUser(null); setSearch(""); }} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
          )}

          {/* Rounds */}
          <div style={{ fontSize: 11, fontWeight: 700, color: faint, marginBottom: 8, textTransform: "uppercase" }}>Rounds</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[3, 5, 7].map(r => (
              <button key={r} onClick={() => setBsRounds(r)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: `${bsRounds === r ? "2px" : "1px"} solid ${bsRounds === r ? "#35c4ff" : border}`, background: bsRounds === r ? "#35c4ff20" : "transparent", color: bsRounds === r ? "#35c4ff" : sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Best of {r}
              </button>
            ))}
          </div>

          {/* Bet Amount */}
          <div style={{ fontSize: 11, fontWeight: 700, color: faint, marginBottom: 8, textTransform: "uppercase" }}>Bet Amount</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[0, 50, 100, 250, 500].map(amt => (
              <button key={amt} onClick={() => setBetAmount(amt)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: `${betAmount === amt ? "2px" : "1px"} solid ${betAmount === amt ? "#35c4ff" : border}`, background: betAmount === amt ? "#35c4ff20" : "transparent", color: betAmount === amt ? "#35c4ff" : sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {amt === 0 ? "Free" : amt}
              </button>
            ))}
          </div>

          {betAmount > 0 && freshGtBalance !== null && freshGtBalance < betAmount && (
            <div style={{ background: "#ef444415", border: "1px solid #ef444430", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#ef4444" }}>
              ⚠️ Insufficient GT! You need {betAmount} GT.
              <button onClick={() => go("wallet")} style={{ marginLeft: 8, background: "#f59e0b", border: "none", borderRadius: 6, padding: "3px 10px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Get GT</button>
            </div>
          )}

          <button onClick={handleSendBsChallenge} disabled={bsSending || !selectedUser || (betAmount > 0 && freshGtBalance < betAmount)}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: selectedUser ? "linear-gradient(135deg,#35c4ff,#1473e6)" : "#35c4ff50", color: "#fff", fontSize: 13, fontWeight: 800, cursor: selectedUser ? "pointer" : "not-allowed", opacity: bsSending ? 0.7 : 1 }}>
            {bsSending ? "Sending..." : `🎨 Challenge${betAmount > 0 ? ` · ${betAmount} GT` : " · Free"}`}
          </button>
        </div>

        {/* How it works */}
        <div style={{ background: "#35c4ff10", border: "1px solid #35c4ff20", borderRadius: 14, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#35c4ff", marginBottom: 8 }}>How it works:</div>
          <div style={{ fontSize: 12, color: sub, lineHeight: 1.8 }}>
            🎯 Both players get the same puzzle<br/>
            ⚡ First to sort all balls wins the round<br/>
            🏆 Best of {bsRounds} rounds — first to {Math.ceil(bsRounds/2)} wins<br/>
            ⏰ If you time out, opponent wins that round<br/>
            🪙 No GT earned — winner takes the bet!
          </div>
        </div>

        <div style={{ height: 80 }} />

        {/* Pending BS Challenge Popup */}
        {bsPendingChallenge && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: 480, background: dark ? "#161720" : "#fff", borderRadius: "24px 24px 0 0", padding: "20px 20px 40px", boxShadow: "0 -8px 40px rgba(0,0,0,0.5)" }}>
              <div style={{ width: 36, height: 3, borderRadius: 2, background: "rgba(128,128,128,0.3)", margin: "0 auto 20px" }} />
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🎨</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: dark ? "#f0f0fa" : "#111", marginBottom: 6 }}>Challenge Sent!</div>
                <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094", marginBottom: 10 }}>
                  Waiting for <strong style={{ color: dark ? "#f0f0fa" : "#111" }}>{bsPendingChallenge.opponentName}</strong> to accept...
                </div>
                {bsPendingChallenge.betAmount > 0 && (
                  <div style={{ display: "inline-block", background: "#f59e0b20", border: "1px solid #f59e0b40", borderRadius: 20, padding: "4px 14px", fontSize: 13, fontWeight: 700, color: "#f59e0b", marginBottom: 10 }}>
                    🪙 {bsPendingChallenge.betAmount} GT frozen
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: "#35c4ff", animation: "gc_pulse 1.5s infinite" }} />
                  <span style={{ fontSize: 12, color: "#35c4ff", fontWeight: 700 }}>Waiting for response...</span>
                </div>
                <div style={{ marginTop: 12, background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "10px 16px" }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Auto-cancels in</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: (bsPendingChallenge.countdown || 30) <= 5 ? "#ef4444" : "#35c4ff" }}>{bsPendingChallenge.countdown || 30}s</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setBsPendingChallenge(null)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: `1px solid ${dark ? "#ffffff15" : "#e5e7eb"}`, background: "transparent", color: dark ? "#8890b0" : "#6b7094", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Hide</button>
                <button onClick={handleCancelBsChallenge} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                  ✕ Cancel{bsPendingChallenge.betAmount > 0 ? ` · Refund ${bsPendingChallenge.betAmount} GT` : ""}
                </button>
              </div>
            </div>
          </div>
        )}

      </Shell>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'DM Sans',sans-serif" }}>
      <Shell title="⚔️ Trivia Battle" showBack onBack={() => setGameMode(null)}>

        {/* GT Balance */}
        <div style={{ background: dark ? "#1a1a0a" : "#fffbeb", border: "1px solid #f59e0b30", borderRadius: 14, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: sub }}>Your GT Balance</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>
            {freshGtBalance !== null ? freshGtBalance : "..."} GT
          </div>
        </div>

        {/* Search Player */}
        <div style={{ background: card, borderRadius: 16, border: `1px solid ${border}`, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: text, marginBottom: 12 }}>🎯 Challenge a Player</div>

          <input
            style={{ width: "100%", padding: "10px 14px", borderRadius: 12, border: `1px solid ${border}`, background: input, color: text, fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }}
            placeholder="Search by name or username..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />

          {/* Search Results */}
          {searching && <div style={{ fontSize: 12, color: faint, textAlign: "center", padding: "8px 0" }}>Searching...</div>}
          {searchResults.length > 0 && !selectedUser && (
            <div style={{ borderRadius: 12, border: `1px solid ${border}`, overflow: "hidden", marginBottom: 10 }}>
              {searchResults.slice(0, 5).map(u => (
                <div key={u.id} onClick={() => { setSelectedUser(u); setSearch(u.name); setSearchResults([]); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${border}`, cursor: "pointer", background: card }}>
                  <Avatar u={u} size={36} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: faint }}>{u.plan || "free"} {u.verified ? "✓" : ""}</div>
                  </div>
                  <span style={{ fontSize: 11, color: "#6c5ce7", fontWeight: 700 }}>Select</span>
                </div>
              ))}
            </div>
          )}

          {/* Selected Player */}
          {selectedUser && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: dark ? "#6c5ce720" : "#f0edff", borderRadius: 12, padding: "10px 14px", marginBottom: 12, border: "1px solid #6c5ce740" }}>
              <Avatar u={selectedUser} size={40} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{selectedUser.name}</div>
                <div style={{ fontSize: 11, color: "#6c5ce7" }}>Selected ✓</div>
              </div>
              <button onClick={() => { setSelectedUser(null); setSearch(""); }} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
          )}

          {/* Bet Amount */}
          <div style={{ fontSize: 11, fontWeight: 700, color: faint, marginBottom: 8, textTransform: "uppercase" }}>Bet Amount</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[0, 50, 100, 250, 500].map(amt => (
              <button key={amt} onClick={() => setBetAmount(amt)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: `${betAmount === amt ? "2px" : "1px"} solid ${betAmount === amt ? "#6c5ce7" : border}`, background: betAmount === amt ? "#6c5ce720" : "transparent", color: betAmount === amt ? "#6c5ce7" : sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {amt === 0 ? "Free" : amt}
              </button>
            ))}
          </div>

          {betAmount > 0 && freshGtBalance !== null && freshGtBalance < betAmount && (
            <div style={{ background: "#ef444415", border: "1px solid #ef444430", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#ef4444" }}>
              ⚠️ Insufficient GT! You need {betAmount} GT.
              <button onClick={() => go("wallet")} style={{ marginLeft: 8, background: "#f59e0b", border: "none", borderRadius: 6, padding: "3px 10px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Get GT</button>
            </div>
          )}

          <button onClick={handleSendChallenge} disabled={sending || !selectedUser || (betAmount > 0 && freshGtBalance < betAmount)}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: selectedUser ? "linear-gradient(135deg,#6c5ce7,#e91e8c)" : "#6c5ce750", color: "#fff", fontSize: 13, fontWeight: 800, cursor: selectedUser ? "pointer" : "not-allowed", opacity: sending ? 0.7 : 1 }}>
            {sending ? "Sending..." : `⚔️ Send Challenge${betAmount > 0 ? ` · ${betAmount} GT` : " · Free"}`}
          </button>
        </div>

        {/* Active Games */}
        {[...(challenges.sent || []), ...(challenges.received || [])].filter(g => g.status === "active").length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: faint, marginBottom: 10, textTransform: "uppercase" }}>🎮 Active Games</div>
            {[...(challenges.sent || []), ...(challenges.received || [])].filter(g => g.status === "active").map(g => (
              <div key={g.id} onClick={async () => { const r = await api(`/trivia/${g.id}/state`); if (r.ok) setActiveGame(r.data.game); }}
                style={{ background: card, borderRadius: 14, border: `1px solid #10b98140`, padding: 14, marginBottom: 10, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar u={g.is_challenger ? g.opponent : g.challenger} size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: text }}>vs {g.is_challenger ? g.opponent?.name : g.challenger?.name}</div>
                    <div style={{ fontSize: 11, color: "#10b981" }}>Round {g.current_round}/{g.total_rounds} · {g.bet_amount > 0 ? `${g.bet_amount} GT` : "Free"}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#10b981" }}>Play →</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Challenge History */}
        {[...(challenges.sent || []), ...(challenges.received || [])].filter(g => ["completed","cancelled","expired"].includes(g.status)).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: faint, marginBottom: 10, textTransform: "uppercase" }}>History</div>
            {[...(challenges.sent || []), ...(challenges.received || [])].filter(g => ["completed","cancelled","expired"].includes(g.status)).slice(0, 5).map(g => (
              <div key={g.id} style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar u={g.is_challenger ? g.opponent : g.challenger} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: text }}>vs {g.is_challenger ? g.opponent?.name : g.challenger?.name}</div>
                  <div style={{ fontSize: 10, color: faint }}>{g.challenger_score} — {g.opponent_score} · {g.bet_amount > 0 ? `${g.bet_amount} GT` : "Free"}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: statusColor(g.status), background: statusColor(g.status) + "20", padding: "2px 8px", borderRadius: 10 }}>{statusLabel(g.status)}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ height: 80 }} />

        {/* Pending Challenge Popup */}
        {pendingChallenge && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: 480, background: dark ? "#161720" : "#fff", borderRadius: "24px 24px 0 0", padding: "20px 20px 40px", boxShadow: "0 -8px 40px rgba(0,0,0,0.5)" }}>
              <div style={{ width: 36, height: 3, borderRadius: 2, background: "rgba(128,128,128,0.3)", margin: "0 auto 20px" }} />
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>⚔️</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: dark ? "#f0f0fa" : "#111", marginBottom: 6 }}>Challenge Sent!</div>
                <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094", marginBottom: 10 }}>
                  Waiting for <strong style={{ color: dark ? "#f0f0fa" : "#111" }}>{pendingChallenge.opponentName}</strong> to accept...
                </div>
                {pendingChallenge.betAmount > 0 && (
                  <div style={{ display: "inline-block", background: "#f59e0b20", border: "1px solid #f59e0b40", borderRadius: 20, padding: "4px 14px", fontSize: 13, fontWeight: 700, color: "#f59e0b", marginBottom: 10 }}>
                    🪙 {pendingChallenge.betAmount} GT frozen
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10b981", animation: "gc_pulse 1.5s infinite" }} />
                  <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>Waiting for response...</span>
                  <style>{`@keyframes gc_pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
                </div>
                <div style={{ marginTop: 12, background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "10px 16px" }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Auto-cancels in</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: (pendingChallenge.countdown || 20) <= 5 ? "#ef4444" : "#f59e0b" }}>{pendingChallenge.countdown || 20}s</div>
                  <div style={{ height: 3, borderRadius: 2, background: "rgba(255,255,255,0.1)", marginTop: 6 }}>
                    <div style={{ height: 3, borderRadius: 2, background: (pendingChallenge.countdown || 20) <= 5 ? "#ef4444" : "#f59e0b", width: `${((pendingChallenge.countdown || 20) / 20) * 100}%`, transition: "width 1s linear" }} />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setPendingChallenge(null)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: `1px solid ${dark ? "#ffffff15" : "#e5e7eb"}`, background: "transparent", color: dark ? "#8890b0" : "#6b7094", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Hide
                </button>
                <button onClick={handleCancelChallenge} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                  ✕ Cancel{pendingChallenge.betAmount > 0 ? ` · Refund ${pendingChallenge.betAmount} GT` : " Challenge"}
                </button>
              </div>
            </div>
          </div>
        )}

      </Shell>
    </div>
  );
}